import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext
} from 'react';
import clsx from 'clsx';
import { Container, Grid, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Title from '../../../../Components/Labels/Title';
import SimpleDivider from '../../../../Components/Dividers/SimpleDivider';
import TextInput from '../../../../Components/Inputs/TextInput';
import SelectInput from '../../../../Components/Inputs/SelectInput';
import Recaptcha from '../../../../Components/Captcha/TurnstileManagedCaptcha';
import BaseButton from '../../../../Components/Buttons/BaseButton';
import ModalProgress from '../../../../Components/Progress/Modal/ModalProgress';

import NewPaymentCard from '../../../../Components/Cards/NewPaymentCard/NewPaymentCard';

import {
  PAYMENT_VALUE_OPTION,
  SELECT_OPTIONS,
  TYPE_OF_PAYMENTS,
  getCouponValue
} from '../../payment_enums';
import { AlertsDispatchContext } from '../../../../Contexts/AlertsContext';

// * API
import { GetCouponAPI } from '../../../../API/Coupons/CouponsAPI';
import { GetInvoicesAPI } from '../../../../API/Invoices/InvoicesAPI';
import { defaultAlertError } from '../../../../Components/Alerts/AlertMessages';
import QuestionMark from '../../../../Components/Adornments/QuestionMark';
import InvoiceHelpDialog from '../../../../Components/Dialogs/InvoiceHelpDialog/InvoiceHelpDialog';

// * Utils
import { HELP_TEXT, COUPON_VALUES } from '../../../../Utils/enums';

import { Company } from '../../../../Configs/general';
import SweetAlert from '../../../../Components/Alerts/SweetAlert';
import { ValidatePartialPaymentAPI } from '../../../../API/Debts/DebtsAPI';
import { ALERT_TYPE } from '../../../../Components/Alerts/alert_enums';
import TurnstileInvisibleCaptcha from '../../../../Components/Captcha/TurnstileInvisibleCaptcha';
import { RecaptchaAction } from '../../../../Enums/recaptcha';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCaptcha } from '../../../../Hooks/useCaptcha';
import { useCaptchaWithFallback } from '../../../../Hooks/useCaptchaWithFallback';

const paymentReferenceSchema = yup.object({
  number: yup
    .string()
    .required(
      `Ingrese el número de ${Company.contractConjugation.regular.singular.main}`
    )
});

const contractSingularDemostrative =
  Company.contractConjugation.capitalized.singular.demonstrative;

const IndividualPaymentEFG = props => {
  const {
    number,
    setNumber,
    setDisabledNext,
    type,
    setType,
    payment,
    setPayment,
    canPayPartially,
    setPaymentValueOption,
    setMethodSelected,
    currentUser
  } = props;

  const setAlert = useContext(AlertsDispatchContext);

  // * STATE HOOKS;
  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const inputRef = useRef(null);

  // * OTHER HOOKS
  const classes = useStyles();
  const isWebSize = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const {
    captchaRef: visibleCaptchaRef,
    handlers: visibleCaptchaHandlers,
    captchaToken: visibleCaptchaToken,
    loading: visibleCaptchaLoading
  } = useCaptcha();
  const {
    captchaRef: invisibleCaptchaRef,
    handlers: invisibleCaptchaHandlers,
    getTokenWithFallback
  } = useCaptchaWithFallback();
  const canSubmit = !loading && visibleCaptchaToken && number && type;

  const { watch, register, setValue, control, handleSubmit, errors } = useForm({
    resolver: yupResolver(paymentReferenceSchema),
    defaultValues: { number }
  });

  const paymentNumber = watch('number');

  useEffect(() => {
    const formIsEmpty = !paymentNumber || loading || !payment || !type;

    if (formIsEmpty || payment.detail.isPaid || payment.detail.isPending) {
      setDisabledNext(true);
    } else {
      // PayU does not allow us to pay coupons with such a small value (100 COP),
      // so disable the Next button when that happens.
      const value = getCouponValue(payment);

      const canPay = value >= COUPON_VALUES.MIN_VALUE;
      setDisabledNext(!canPay);
      if (!canPay) {
        setAlert({
          type: 'info',
          message: `Solo se permite realizar pagos mayores a ${COUPON_VALUES.MIN_VALUE} pesos.`
        });
      }
    }
  }, [type, paymentNumber, setDisabledNext, loading, payment, setAlert]);

  const handleType = useCallback(
    e => {
      setType(e.target.value);
      setValue('number', null);
      setNumber('');
      setPayment([]);
    },
    [setType, setNumber, setPayment, setValue]
  );

  useEffect(() => {
    setPayment([]);
    setNumber(paymentNumber);
  }, [paymentNumber, setNumber, setPayment]);

  const onHelpClick = useCallback(() => {
    setHelpDialogOpen(true);
  }, []);

  const onSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    // set states
    setLoading(true);
    setPayment([]);
    setMethodSelected('');
    setPaymentValueOption('');

    setDisabledNext(true);

    const captchaValue = visibleCaptchaToken;
    visibleCaptchaHandlers.reset();

    let paymentData;

    // api fetch
    if (type === TYPE_OF_PAYMENTS.coupon) {
      const { success, data, error } = await GetCouponAPI(number, captchaValue);
      if (success) {
        const couponData = data.data;
        paymentData = [
          {
            type: TYPE_OF_PAYMENTS.coupon,
            detail: {
              ...couponData,
              couponId: couponData.id,
              couponValue: couponData.value
            }
          }
        ];
      } else {
        defaultAlertError(error, setAlert, 'couponId');
        setLoading(false);
        return;
      }
    } else if (type === TYPE_OF_PAYMENTS.contract) {
      const { success, data, error } = await GetInvoicesAPI(
        number,
        captchaValue
      );

      if (success) {
        paymentData = [
          { type: TYPE_OF_PAYMENTS.contract, detail: data.data[0] }
        ];
      } else {
        defaultAlertError(error, setAlert);
        setLoading(false);
        return;
      }
    }

    const { couponId, contractId } = paymentData[0].detail;

    const userToken = currentUser ? currentUser.token : null;

    let recaptchaToken;
    if (!userToken) {
      recaptchaToken = await getTokenWithFallback();
    }

    const validateResponse = await ValidatePartialPaymentAPI(
      contractId,
      couponId,
      userToken,
      recaptchaToken
    );

    if (validateResponse.success) {
      const {
        valid,
        canPayPartially: canPayPartial,
        isPartialCoupon
      } = validateResponse.data.data;
      paymentData = [
        {
          ...paymentData[0],
          canBePaidPartially: canPayPartial && valid,
          isPartialCoupon
        }
      ];
    } else {
      paymentData = [{ ...paymentData[0], canBePaidPartially: false }];
    }

    setPaymentValueOption(
      paymentData[0].canBePaidPartially ? '' : PAYMENT_VALUE_OPTION.TotalCoupon
    );

    setPayment(paymentData);

    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  return (
    <>
      <TurnstileInvisibleCaptcha
        captchaRef={invisibleCaptchaRef}
        onResolved={invisibleCaptchaHandlers.onResolved}
        onExpired={invisibleCaptchaHandlers.onExpired}
        onError={invisibleCaptchaHandlers.onError}
        onLoaded={invisibleCaptchaHandlers.onLoaded}
        action={RecaptchaAction.ValidatePartialPayments}
      />
      <InvoiceHelpDialog
        open={helpDialogOpen}
        type={type}
        onClose={() => setHelpDialogOpen(false)}
        title={HELP_TEXT[type]}
      />
      {visibleCaptchaLoading && (
        <ModalProgress
          id="QueryPayment_progress_loadingCaptcha"
          message="Cargando"
        />
      )}
      <Container
        maxWidth="md"
        className={`${classes.root} ${
          visibleCaptchaLoading ? classes.captchaLoading : ''
        }`}
      >
        {loading && (
          <ModalProgress
            id="QueryPayment_progress_loading"
            message="Buscando"
          />
        )}
        <Grid container spacing={4} className={classes.formContainer}>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <SelectInput
              id="QueryPayment_select_paymentMethod"
              value={type}
              onChange={handleType}
              disabled={loading}
              label="Referencia"
              fullWidth
              options={SELECT_OPTIONS}
            />
          </Grid>
          <Grid item md={4} sm={4} xs={12} className={classes.inputContainer}>
            <TextInput
              id="number"
              inputRef={register}
              control={control}
              name="number"
              autoFocus
              disabled={loading}
              error={Boolean(errors.number)}
              label={`Número ${
                type === TYPE_OF_PAYMENTS.contract
                  ? `de ${Company.contractConjugation.regular.singular.main}`
                  : 'del cupón'
              }`}
              InputProps={{
                endAdornment: <QuestionMark onClick={onHelpClick} />,
                inputProps: {
                  type: 'number',
                  inputMode: 'numeric'
                }
              }}
              required={true}
              helperText={
                errors && errors.number
                  ? errors.number.message || errors.number.types.message
                  : ''
              }
              fullWidth
            />
          </Grid>
          <Grid
            item
            sm={4}
            xs={12}
            ref={inputRef}
            className={clsx([classes.inputContainer])}
          >
            <Recaptcha
              captchaRef={visibleCaptchaRef}
              referenceEl={inputRef}
              locale="es"
              heightScale={0.9}
              onResolved={visibleCaptchaHandlers.onResolved}
              onExpired={visibleCaptchaHandlers.onExpired}
              onError={visibleCaptchaHandlers.onError}
              onLoaded={visibleCaptchaHandlers.onLoaded}
            />
          </Grid>
          <Grid item xs={12} className={classes.consultContainer}>
            <BaseButton
              id="QueryPayment_button_submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!canSubmit}
              fullWidth={!isWebSize}
              color="primary"
              variant="outlined"
            >
              Consultar
            </BaseButton>
          </Grid>
        </Grid>
        <SimpleDivider withoutMargin />
        {payment && (
          <div style={{ marginBottom: '80px' }}>
            <Title
              text={`Detalle ${
                payment.type === TYPE_OF_PAYMENTS.coupon
                  ? 'del cupón'
                  : 'de la factura'
              }`}
              className={classes.resultTitle}
            />
            <NewPaymentCard
              type={payment.type}
              newTariff={false}
              onChangeNewTariff={() => null}
              details={{
                ...payment.detail
              }}
            />
            {canPayPartially && (
              <SweetAlert
                type={ALERT_TYPE.INFO}
                classes={{ root: classes.sweetAlert }}
                message={
                  <>
                    {contractSingularDemostrative} puede realizar abonos a su
                    deuda actual. Si la deuda cumple con las condiciones para
                    abonar, al continuar al siguiente paso encontrarás la opción
                    de pago <b>Otro valor</b>.
                  </>
                }
              />
            )}
          </div>
        )}
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-around',
    padding: theme.spacing(0),
    marginBottom: 200,
    // * Responsive
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(),
      marginBottom: theme.spacing()
    }
  },
  consultContainer: {
    textAlign: 'end',
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingBottom: '0 !important'
    },
    padding: theme.spacing(2, 0)
  },
  inputContainer: {
    // * Mobile
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginTop: theme.spacing(),
      paddingTop: '0 !important',
      paddingBottom: '0 !important'
    },
    padding: 0
  },
  recaptchaContainer: {
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      marginBottom: 24
    }
  },
  captchaWeb: {
    // * Mobile
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  resultTitle: {
    marginTop: theme.spacing(3)
  },
  formContainer: {
    marginBottom: theme.spacing(2)
  },
  captchaMobile: {
    // * Mobile
    paddingTop: theme.spacing(5),
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(1)
    },
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  captchaLoading: {
    visibility: 'hidden'
  },
  sweetAlert: {
    marginTop: theme.spacing(2)
  }
}));

export default IndividualPaymentEFG;
